﻿import { api } from '@/lib/api/client';
import type { BandStatus, BandVariant } from './bandTypes';
import type {
  PaperComparison,
  PaperOverview,
  PaperProfile,
  PaperRoundHistory,
} from './paperTypes';
import type { SwingAccountKey, SwingAccountTab, SwingStatus } from './swingTypes';
import type { TradingProfile } from './types';

/**
 * AI 모의투자 API. (18단계)
 *
 * 🔴 조회만 있다
 *   모의투자는 배치가 굴린다. 화면에서 사고파는 요청을 보내지 않는다.
 *   수동 매수가 섞이면 "AI 가 자기 추천대로 굴렸으면" 이라는 전제가 깨진다.
 *
 * 계좌 초기화는 관리자 화면 소관이다.
 */

/** 한 프로필 탭 전체. 계좌 요약 · 곡선 · 보유 · 청산 · 회고. */
export function fetchPaperOverview(profile: PaperProfile) {
  return api.get<PaperOverview>(`/trading/paper/${profile}`);
}

/** 비교 탭. 세 프로필 통계를 나란히. */
export function fetchPaperComparison() {
  return api.get<PaperComparison>('/trading/paper/comparison');
}

/**
 * 지금 회차에 쉬는 추천 프로필. 그 계좌 탭 · 추천 탭을 숨길 때 쓴다. (2026-10-10 사용자 결정 · 141번 10.6)
 * 예) 4회차 진행 중 → ['MODERATE', 'AGGRESSIVE'] · 3회차 · 회차 사이 → []
 */
export function fetchRestingProfiles() {
  return api.get<TradingProfile[]>('/trading/paper/resting');
}

/**
 * 밴드 매매 탭 전체. 계좌·보유(사다리)·감시 대상·청산 이력 한 벌. (docs/impl/85)
 *
 * @param variant 계좌 변형. 없으면 보수(CONSERVATIVE)다 (V119, docs/impl/98)
 */
export function fetchBandStatus(variant?: BandVariant) {
  return api.get<BandStatus>('/trading/band', variant ? { variant } : undefined);
}

/**
 * 화면에 보일 국내 스윙 계좌. (2026-10-10 · 141번 12.6-5)
 * 예) 4회차 진행 중 → [{key:'SWING_A',label:'스윙(6개월)'}, {key:'SWING_C',label:'스윙(3개월)'}] · 3회차까지 → []
 */
export function fetchSwingAccounts(country: 'KR' | 'US' = 'KR') {
  // 🇺🇸 country=US 면 미국 화면 탭(SWING_US_A) — 국내 AI 모의투자 탭 줄은 KR(2026-10-10)
  return api.get<SwingAccountTab[]>('/trading/swing/accounts', { country });
}

/** 국내 스윙 계좌 탭 한 벌. 계좌 카드 · 보유 · 감시 대상 · 섹터 · 청산 · 회고 · 설정 */
export function fetchSwingStatus(account: SwingAccountKey) {
  return api.get<SwingStatus>('/trading/swing', { account });
}

/**
 * 회차 이력 목록. 최근 회차부터. (2026-09-03)
 * 회고는 비어 온다 - 목록에서 안 읽고, 회차가 쌓이면 응답이 무거워진다.
 */
export function fetchPaperRounds() {
  return api.get<PaperRoundHistory[]>('/trading/paper/rounds');
}

/** 회차 하나. 계좌별 성적 + 종합 분석 + 그 기간에 쓰인 회고. */
export function fetchPaperRound(roundNo: number) {
  return api.get<PaperRoundHistory>(`/trading/paper/rounds/${roundNo}`);
}
